import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type { Virtualizer } from '@tanstack/react-virtual';

import { isTouchPointer, noop } from 'helpers/utils';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import type { SetTimeOut } from 'types/utils';

type UseVirtualizerAutoScrollParams = {
  tableData: any[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  parentRef: { current: HTMLDivElement | null };
  autoScroll: {
    reverseScroll: boolean;
    debounced: boolean;
    visibilityChange: boolean;
    scrollOnChange?: string;
  };
  delayInMs?: number;
};

const isTouchDevice = isTouchPointer();

const useVirtualizerAutoScroll = ({
  tableData,
  rowVirtualizer,
  parentRef,
  autoScroll: {
    reverseScroll,
    debounced: debouncedAutoScroll,
    visibilityChange: autoScrollOnVisibilityChange,
    scrollOnChange,
  },
  delayInMs = 3000,
}: UseVirtualizerAutoScrollParams) => {
  const interactionTimeoutRef = useRef<SetTimeOut>();
  const isUserInteracting = useRef(false);
  const isTabVisible = useRef(true);

  const scrollToIndex = reverseScroll ? tableData.length - 1 : 0;

  const scrollToFirstItem = useCallback(() => {
    if (!debouncedAutoScroll || !isTabVisible.current) return;

    requestAnimationFrame(() => {
      rowVirtualizer.scrollToIndex(scrollToIndex, {
        align: 'center',
        behavior: 'auto',
      });
    });
  }, [debouncedAutoScroll, rowVirtualizer, scrollToIndex]);

  /** Stable reference to scrollToFirstItem function */
  const scrollToFirstItemRef = useRef(scrollToFirstItem);

  useEffect(() => {
    scrollToFirstItemRef.current = scrollToFirstItem;
  }, [scrollToFirstItem]);

  // Scroll on Mount
  useLayoutEffect(() => {
    scrollToFirstItemRef.current();

    return () => {
      clearTimeout(interactionTimeoutRef.current);
      isUserInteracting.current = false;
    };
  }, []);

  // Scroll on dependency changes immediately
  useDidUpdateEffect(() => {
    scrollToFirstItemRef.current();

    clearTimeout(interactionTimeoutRef.current);
    isUserInteracting.current = false;
  }, [scrollOnChange]);

  // Scroll on scrollIndex change, pauses when user has recently interacted
  useDidUpdateEffect(() => {
    if (!isUserInteracting.current) {
      scrollToFirstItemRef.current();
    }
  }, [scrollToIndex]);

  useEffect(() => {
    const tableContainer = parentRef.current;
    if (isTouchDevice || !tableContainer) {
      return noop;
    }

    const handleMouseEnter = () => {
      clearTimeout(interactionTimeoutRef.current);
      isUserInteracting.current = true;
    };

    const handleMouseLeave = () => {
      interactionTimeoutRef.current = setTimeout(() => {
        scrollToFirstItemRef.current();
        isUserInteracting.current = false;
      }, delayInMs);
    };

    const startEvent = 'mouseenter';
    const endEvent = 'mouseleave';

    tableContainer.addEventListener(startEvent, handleMouseEnter);
    tableContainer.addEventListener(endEvent, handleMouseLeave);

    return () => {
      tableContainer.removeEventListener(startEvent, handleMouseEnter);
      tableContainer.removeEventListener(endEvent, handleMouseLeave);
    };

    // ! parent ref is a useState variable
  }, [parentRef, delayInMs]);

  useEffect(() => {
    if (!isTouchDevice) {
      return noop;
    }

    if (rowVirtualizer.isScrolling) {
      isUserInteracting.current = true;
    } else {
      interactionTimeoutRef.current = setTimeout(() => {
        scrollToFirstItemRef.current();
        isUserInteracting.current = false;
      }, delayInMs);
    }

    return () => {
      clearTimeout(interactionTimeoutRef.current);
    };
  }, [rowVirtualizer.isScrolling, delayInMs]);

  // Scroll on page visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        isTabVisible.current = true;
        scrollToFirstItemRef.current();
      } else {
        isTabVisible.current = false;
      }
    };

    if (autoScrollOnVisibilityChange) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoScrollOnVisibilityChange]);
};

export default useVirtualizerAutoScroll;
